import { CommonModule } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import dayjs from 'dayjs';
import { ConfirmationService, FilterService, PrimeNGConfig } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { MessagesModule } from 'primeng/messages';
import { ToastModule } from 'primeng/toast';
import { SpinnerComponent } from './core/components/spinner/spinner.component';
import { AppLayoutModule } from './core/layout/app.layout.module';
import { TenantSettingsService } from './core/services/tenant-settings.service';
import { AppSettingsService } from './core/services/app-settings.service';

import * as i18nData from '../assets/i18n/it.json';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    CommonModule,
    RouterOutlet,
    AppLayoutModule,
    ToastModule,
    SpinnerComponent,
    ConfirmDialogModule,
    MessagesModule,
  ],
  providers: [ConfirmationService],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit {
  filterService = inject(FilterService);
  primeNGConfig = inject(PrimeNGConfig);
  private tenantSettingsService = inject(TenantSettingsService);
  private appSettingsService = inject(AppSettingsService);
  title = 'app';

  private getContrastColor(hex: string): string {
    const h = hex.replace('#', '');
    const r = parseInt(h.substring(0, 2), 16);
    const g = parseInt(h.substring(2, 4), 16);
    const b = parseInt(h.substring(4, 6), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.5 ? '#1e293b' : '#ffffff';
  }

  ngOnInit(): void {
    this.primeNGConfig.setTranslation(i18nData);

    this.tenantSettingsService.loadTenantImages().subscribe(() => {
      const faviconUrl = this.tenantSettingsService.getFaviconUrl() || 'assets/favicon.png';
      const link = document.getElementById('app-favicon') as HTMLLinkElement;
      if (link) link.href = faviconUrl;
    });

    this.appSettingsService.loadSettings().subscribe((settings) => {
      const root = document.documentElement;
      if (settings.topbarBgColor) {
        root.style.setProperty('--topbar-bg-color', settings.topbarBgColor);
        root.style.setProperty('--topbar-text-color', this.getContrastColor(settings.topbarBgColor));
      }
      if (settings.loginBgColor) {
        root.style.setProperty('--login-bg-color', settings.loginBgColor);
        root.style.setProperty('--login-text-color', this.getContrastColor(settings.loginBgColor));
      }
    });

    this.filterService.register('customDateRange', (value: Date, filter: Date[]): boolean => {
      if (filter && filter[0] && filter[1]) {
        const startDate = dayjs(filter[0]).startOf('day');
        const endDate = dayjs(filter[1]).endOf('day');
        const valueDate = dayjs(value);
        return valueDate.isAfter(startDate) && valueDate.isBefore(endDate);
      }
      return true;
    });
  }
}
